import React, { useState, useEffect } from 'react'
import {
  Box,
  Flex,
  Heading,
  Text,
  Input,
  Button,
  Alert,
  AlertIcon,
  AlertDescription,
} from '@chakra-ui/react'
import {
  collection,
  getDocs,
  addDoc,
  updateDoc,
  deleteDoc,
  doc,
  serverTimestamp,
} from 'firebase/firestore'
import { db } from '../config/firebase'
import { getYouTubeId } from '../utils/youtubeUtils'
import { isYouTubeShorts } from '../utils/deviceDetect'

const VIDEOS_COLLECTION = 'videos'

interface Video {
  id: string
  name?: string
  youtubeUrl?: string
  videoId?: string
  order?: number
  createdAt?: Date
  updatedAt?: Date
}

const VideoManager = () => {
  const [videos, setVideos] = useState<Video[]>([])
  const [loading, setLoading] = useState(true)
  const [newName, setNewName] = useState('')
  const [newUrl, setNewUrl] = useState('')
  const [editingId, setEditingId] = useState<string | null>(null)
  const [editingName, setEditingName] = useState('')
  const [editingUrl, setEditingUrl] = useState('')
  const [error, setError] = useState('')
  const [copiedVideoId, setCopiedVideoId] = useState<string | null>(null)

  const fetchVideos = async () => {
    if (!db) {
      setError('Firebase가 설정되지 않았습니다.')
      setLoading(false)
      return
    }
    try {
      const snapshot = await getDocs(collection(db, VIDEOS_COLLECTION))
      const list = snapshot.docs
        .map((d) => ({
          id: d.id,
          ...d.data(),
          order: d.data().order ?? 999999,
          createdAt: d.data().createdAt?.toDate?.() ?? d.data().createdAt,
          updatedAt: d.data().updatedAt?.toDate?.() ?? d.data().updatedAt,
        }))
        .sort((a, b) => (a.order ?? 999999) - (b.order ?? 999999)) as Video[]
      setVideos(list)
    } catch (err) {
      setError(
        '비디오 목록을 불러오는데 실패했습니다: ' + (err as Error).message,
      )
    } finally {
      setLoading(false)
    }
  }

  const addVideo = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!newName.trim()) {
      setError('이름을 입력해주세요')
      return
    }
    if (!newUrl.trim()) {
      setError('YouTube URL을 입력해주세요')
      return
    }
    const videoId = getYouTubeId(newUrl)
    if (!videoId) {
      setError('유효한 YouTube URL을 입력해주세요')
      return
    }
    if (!db) {
      setError('Firebase가 설정되지 않았습니다.')
      return
    }
    try {
      const maxOrder =
        videos.length > 0 ? Math.max(...videos.map((v) => v.order ?? 0)) : -1
      await addDoc(collection(db, VIDEOS_COLLECTION), {
        name: newName.trim(),
        youtubeUrl: newUrl.trim(),
        videoId: videoId,
        order: maxOrder + 1,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      })
      setNewName('')
      setNewUrl('')
      setError('')
      await fetchVideos()
    } catch (err) {
      setError('비디오 추가에 실패했습니다: ' + (err as Error).message)
    }
  }

  const updateVideo = async (id: string) => {
    if (!editingName.trim()) {
      setError('이름을 입력해주세요')
      return
    }
    if (!editingUrl.trim()) {
      setError('YouTube URL을 입력해주세요')
      return
    }
    const videoId = getYouTubeId(editingUrl)
    if (!videoId) {
      setError('유효한 YouTube URL을 입력해주세요')
      return
    }
    if (!db) {
      setError('Firebase가 설정되지 않았습니다.')
      return
    }
    try {
      await updateDoc(doc(db, VIDEOS_COLLECTION, id), {
        name: editingName.trim(),
        youtubeUrl: (editingUrl ?? '').trim(),
        videoId: videoId,
        updatedAt: serverTimestamp(),
      })
      setEditingId(null)
      setEditingName('')
      setEditingUrl('')
      setError('')
      await fetchVideos()
    } catch (err) {
      setError('비디오 수정에 실패했습니다: ' + (err as Error).message)
    }
  }

  const deleteVideo = async (id: string) => {
    if (!window.confirm('정말로 이 비디오를 삭제하시겠습니까?')) return
    if (!db) {
      setError('Firebase가 설정되지 않았습니다.')
      return
    }
    try {
      await deleteDoc(doc(db, VIDEOS_COLLECTION, id))
      await fetchVideos()
    } catch (err) {
      setError('비디오 삭제에 실패했습니다: ' + (err as Error).message)
    }
  }

  const startEdit = (video: Video) => {
    setEditingId(video.id)
    setEditingName(video.name ?? '')
    setEditingUrl(video.youtubeUrl ?? '')
  }

  const cancelEdit = () => {
    setEditingId(null)
    setEditingName('')
    setEditingUrl('')
  }

  const moveOrder = async (video: Video, direction: 'up' | 'down') => {
    if (!db) return
    const idx = videos.findIndex((v) => v.id === video.id)
    if (idx < 0) return
    const targetIdx = direction === 'up' ? idx - 1 : idx + 1
    if (targetIdx < 0 || targetIdx >= videos.length) return
    const target = videos[targetIdx]
    const newOrder = target.order ?? targetIdx
    const myNewOrder = video.order ?? idx
    try {
      await updateDoc(doc(db, VIDEOS_COLLECTION, video.id), {
        order: newOrder,
        updatedAt: serverTimestamp(),
      })
      await updateDoc(doc(db, VIDEOS_COLLECTION, target.id), {
        order: myNewOrder,
        updatedAt: serverTimestamp(),
      })
      await fetchVideos()
    } catch (err) {
      setError('순서 변경에 실패했습니다: ' + (err as Error).message)
    }
  }

  const copyShareLink = async (video: Video) => {
    const shareUrl = video.youtubeUrl ?? ''
    try {
      await navigator.clipboard.writeText(shareUrl)
      setCopiedVideoId(video.id)
      setTimeout(() => setCopiedVideoId(null), 2000)
    } catch (err) {
      const textArea = document.createElement('textarea')
      textArea.value = shareUrl
      document.body.appendChild(textArea)
      textArea.select()
      document.execCommand('copy')
      document.body.removeChild(textArea)
      setCopiedVideoId(video.id)
      setTimeout(() => setCopiedVideoId(null), 2000)
    }
  }

  useEffect(() => {
    fetchVideos()
  }, [])

  if (loading) {
    return (
      <Box p={8} textAlign="center">
        <Text color="gray.600">비디오 목록을 불러오는 중...</Text>
      </Box>
    )
  }

  return (
    <Box p={{ base: 4, sm: 8 }}>
      <Heading as="h2" size="lg" color="gray.900" mb={8}>
        비디오 관리
      </Heading>

      <Box
        as="form"
        onSubmit={addVideo}
        bg="gray.50"
        borderRadius="lg"
        p={6}
        mb={8}
      >
        <Flex flexDirection={{ base: 'column', sm: 'row' }} gap={4}>
          <Input
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            placeholder="이름 (예: 01. Practical Smart Solutions)"
            w={{ base: '100%', sm: '256px' }}
            borderColor="gray.300"
            _focus={{
              borderColor: 'blue.500',
              boxShadow: '0 0 0 1px var(--chakra-colors-blue-500)',
            }}
          />
          <Input
            type="url"
            value={newUrl}
            onChange={(e) => setNewUrl(e.target.value)}
            placeholder="YouTube URL"
            flex={1}
            borderColor="gray.300"
            _focus={{
              borderColor: 'blue.500',
              boxShadow: '0 0 0 1px var(--chakra-colors-blue-500)',
            }}
          />
          <Button
            type="submit"
            colorScheme="green"
            px={6}
            py={3}
            whiteSpace="nowrap"
          >
            추가
          </Button>
        </Flex>
      </Box>

      {error && (
        <Alert status="error" borderRadius="lg" mb={6}>
          <AlertIcon />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      <Box>
        {videos.length === 0 ? (
          <Box textAlign="center" py={12}>
            <Text color="gray.600" fontSize="lg" mb={2}>
              등록된 비디오가 없습니다.
            </Text>
            <Text color="gray.500">위에서 YouTube URL을 추가해보세요.</Text>
          </Box>
        ) : (
          videos.map((video, index) => (
            <Box
              key={video.id}
              bg="white"
              border="1px solid"
              borderColor="gray.200"
              borderRadius="lg"
              p={6}
              mb={4}
              _hover={{ boxShadow: 'md' }}
              transition="box-shadow 0.2s"
            >
              <Flex
                flexDirection={{ base: 'column', lg: 'row' }}
                alignItems={{ base: 'stretch', lg: 'center' }}
                justifyContent="space-between"
                gap={4}
              >
                <Flex alignItems="center" gap={4} flex={1} minW={0}>
                  <Flex
                    w={12}
                    h={12}
                    bg="blue.600"
                    color="white"
                    borderRadius="full"
                    alignItems="center"
                    justifyContent="center"
                    fontWeight="bold"
                    fontSize="lg"
                    flexShrink={0}
                  >
                    #{index + 1}
                  </Flex>
                  <Box flex={1} minW={0}>
                    <Box mb={1}>
                      {editingId === video.id ? (
                        <Input
                          value={editingName}
                          onChange={(e) => setEditingName(e.target.value)}
                          placeholder="이름"
                          size="sm"
                          mb={2}
                          borderColor="gray.300"
                        />
                      ) : (
                        <Text fontWeight="semibold" color="gray.900">
                          {video.name ?? '이름 없음'}
                        </Text>
                      )}
                    </Box>
                    <Box mb={0.5}>
                      {editingId === video.id ? (
                        <Input
                          type="url"
                          value={editingUrl}
                          onChange={(e) => setEditingUrl(e.target.value)}
                          placeholder="YouTube URL"
                          size="sm"
                          borderColor="gray.300"
                        />
                      ) : (
                        <Flex alignItems="center" gap={2} flexWrap="wrap">
                          <Text
                            fontSize="sm"
                            color="gray.600"
                            wordBreak="break-all"
                          >
                            {video.youtubeUrl}
                          </Text>
                          {isYouTubeShorts(video.youtubeUrl) && (
                            <Text
                              as="span"
                              fontSize="xs"
                              bg="red.100"
                              color="red.800"
                              px={2}
                              py={1}
                              borderRadius="full"
                              fontWeight="medium"
                            >
                              Shorts
                            </Text>
                          )}
                        </Flex>
                      )}
                    </Box>
                    <Text fontSize="xs" color="gray.500">
                      Video ID: {video.videoId}
                    </Text>
                  </Box>
                </Flex>

                <Flex flexWrap="wrap" gap={2}>
                  {editingId === video.id ? (
                    <>
                      <Button
                        size="sm"
                        colorScheme="blue"
                        onClick={() => updateVideo(video.id)}
                      >
                        저장
                      </Button>
                      <Button size="sm" colorScheme="gray" onClick={cancelEdit}>
                        취소
                      </Button>
                    </>
                  ) : (
                    <>
                      <Button
                        size="sm"
                        colorScheme="gray"
                        onClick={() => moveOrder(video, 'up')}
                        isDisabled={index === 0}
                        title="위로"
                      >
                        ↑
                      </Button>
                      <Button
                        size="sm"
                        colorScheme="gray"
                        onClick={() => moveOrder(video, 'down')}
                        isDisabled={index === videos.length - 1}
                        title="아래로"
                      >
                        ↓
                      </Button>
                      <Button
                        size="sm"
                        colorScheme="green"
                        onClick={() => copyShareLink(video)}
                      >
                        {copiedVideoId === video.id ? '복사됨!' : '공유'}
                      </Button>
                      <Button
                        size="sm"
                        colorScheme="blue"
                        onClick={() => startEdit(video)}
                      >
                        수정
                      </Button>
                      <Button
                        size="sm"
                        colorScheme="red"
                        onClick={() => deleteVideo(video.id)}
                      >
                        삭제
                      </Button>
                    </>
                  )}
                </Flex>
              </Flex>
            </Box>
          ))
        )}
      </Box>
    </Box>
  )
}

export default VideoManager
